import { __decorate } from "tslib";
import { ArrayDecoder, AutoEncoder, BooleanDecoder, EmailDecoder, field, RecordDecoder, StringDecoder } from '@simonbackx/simple-encoding';
import { Formatter } from '@stamhoofd/utility';
import { v4 as uuidv4 } from 'uuid';
import { File } from '../files/File.js';
export class EmailInformation extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.markedAsSpam = false;
        this.hardBounce = false;
        this.unsubscribedMarketing = false;
        this.unsubscribedAll = false;
    }
}
__decorate([
    field({ decoder: StringDecoder })
], EmailInformation.prototype, "email", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], EmailInformation.prototype, "markedAsSpam", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], EmailInformation.prototype, "hardBounce", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], EmailInformation.prototype, "unsubscribedMarketing", void 0);
__decorate([
    field({ decoder: BooleanDecoder })
], EmailInformation.prototype, "unsubscribedAll", void 0);
export class Replacement extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.value = '';
    }
}
__decorate([
    field({ decoder: StringDecoder })
], Replacement.prototype, "token", void 0);
__decorate([
    field({ decoder: StringDecoder })
], Replacement.prototype, "value", void 0);
__decorate([
    field({ decoder: StringDecoder, optional: true })
], Replacement.prototype, "html", void 0);
export function replaceEmailHtml(html, replacements) {
    let replacedHtml = html;
    for (const replacement of replacements) {
        replacedHtml = replacedHtml.replace(new RegExp(Formatter.escapeRegex('{{' + replacement.token + '}}'), 'g'), replacement.html || Formatter.escapeHtml(replacement.value));
    }
    // Do two passes to support recursive replacements
    for (const replacement of replacements) {
        replacedHtml = replacedHtml.replace(new RegExp(Formatter.escapeRegex('{{' + replacement.token + '}}'), 'g'), replacement.html || Formatter.escapeHtml(replacement.value));
    }
    return replacedHtml;
}
export function replaceEmailText(text, replacements) {
    let replacedText = text;
    for (const replacement of replacements) {
        replacedText = replacedText.replace(new RegExp(Formatter.escapeRegex('{{' + replacement.token + '}}'), 'g'), replacement.value);
    }
    // Two passes to support recursive replacements
    for (const replacement of replacements) {
        replacedText = replacedText.replace(new RegExp(Formatter.escapeRegex('{{' + replacement.token + '}}'), 'g'), replacement.value);
    }
    return replacedText;
}
export class Recipient extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.firstName = null;
        this.lastName = null;
        this.replacements = [];
        this.headers = null;
        /**
         * Set this to create a replacement called signInUrl, which will auto sign in/sign up the user
         * Note: the e-mail is matched with the user id, if it doesn't match, the sign-in button will contain a simple (non smart) url
         */
        this.userId = null;
        /// For reference and filtering
        /**
         * @deprecated
         * Use types instead
         */
        this.type = null;
        /// For reference and filtering
        this.types = [];
    }
    getDefaultReplacements() {
        var _a, _b;
        return [
            Replacement.create({
                token: 'firstName',
                value: (_a = this.firstName) !== null && _a !== void 0 ? _a : '',
            }),
            Replacement.create({
                token: 'lastName',
                value: (_b = this.lastName) !== null && _b !== void 0 ? _b : '',
            }),
            Replacement.create({
                token: 'email',
                value: this.email,
            }),
            Replacement.create({
                token: 'greeting',
                value: this.firstName ? $t(`2e5b9210-6e4a-44b2-ab5a-b3883c687dc6`, { name: this.firstName }) : $t(`Hallo!`),
            }),
        ];
    }
    merge(recipient) {
        var _a;
        this.firstName = this.firstName !== null && this.firstName.length > 0 ? this.firstName : recipient.firstName;
        this.lastName = this.lastName !== null && this.lastName.length > 0 ? this.lastName : recipient.lastName;
        for (const replacement of recipient.replacements) {
            const existing = this.replacements.find(r => r.token == replacement.token);
            if (!existing) {
                this.replacements.push(replacement);
            }
            else {
                if (existing.value.length == 0) {
                    existing.value = replacement.value;
                }
            }
        }
        this.userId = (_a = this.userId) !== null && _a !== void 0 ? _a : recipient.userId;
        this.types = Formatter.uniqueArray(this.types.concat(recipient.types));
    }
    /**
     * Remove duplicate replacements, keeping the last added replacements
     */
    removeDuplicates() {
        const replacements = [];
        for (const replacement of this.replacements.slice().reverse()) {
            if (!replacements.find(r => r.token === replacement.token)) {
                replacements.unshift(replacement);
            }
        }
        this.replacements = replacements;
    }
}
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], Recipient.prototype, "firstName", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 112 })
], Recipient.prototype, "lastName", void 0);
__decorate([
    field({ decoder: EmailDecoder })
], Recipient.prototype, "email", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Replacement) })
], Recipient.prototype, "replacements", void 0);
__decorate([
    field({ decoder: new RecordDecoder(StringDecoder, StringDecoder), version: 209, nullable: true })
], Recipient.prototype, "headers", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 80 })
], Recipient.prototype, "userId", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true, version: 96 })
], Recipient.prototype, "type", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(StringDecoder), optional: true })
], Recipient.prototype, "types", void 0);
export class EmailAttachment extends AutoEncoder {
    constructor() {
        super(...arguments);
        /**
         * @deprecated
         * Legacy email attachments have a base64 encoded content.
         * This became deprecated because the size became too large to store efficiently. Instead we now require the usage of (private) files.
         */
        this.content = null;
        this.file = null;
    }
    get bytes() {
        if (this.file) {
            return this.file.size;
        }
        if (!this.content) {
            return 0;
        }
        // Calculates bytes of base64 string
        return Math.ceil((this.content.length / 4) * 3) - (this.content.endsWith('==') ? 2 : this.content.endsWith('=') ? 1 : 0);
    }
    get icon() {
        var _a, _b;
        return (_b = (_a = this.file) === null || _a === void 0 ? void 0 : _a.icon) !== null && _b !== void 0 ? _b : 'file';
    }
}
__decorate([
    field({ decoder: StringDecoder, defaultValue: () => uuidv4() })
], EmailAttachment.prototype, "id", void 0);
__decorate([
    field({ decoder: StringDecoder })
], EmailAttachment.prototype, "filename", void 0);
__decorate([
    field({ decoder: StringDecoder })
], EmailAttachment.prototype, "contentType", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], EmailAttachment.prototype, "content", void 0);
__decorate([
    field({ decoder: File, nullable: true, version: 375 })
], EmailAttachment.prototype, "file", void 0);
export class EmailRequest extends AutoEncoder {
    constructor() {
        super(...arguments);
        this.text = null;
        this.html = null;
        this.attachments = [];
        this.defaultReplacements = [];
    }
}
__decorate([
    field({ decoder: StringDecoder })
], EmailRequest.prototype, "emailId", void 0);
__decorate([
    field({ decoder: StringDecoder })
], EmailRequest.prototype, "subject", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Recipient) })
], EmailRequest.prototype, "recipients", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], EmailRequest.prototype, "text", void 0);
__decorate([
    field({ decoder: StringDecoder, nullable: true })
], EmailRequest.prototype, "html", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(EmailAttachment), version: 11 })
], EmailRequest.prototype, "attachments", void 0);
__decorate([
    field({ decoder: new ArrayDecoder(Replacement), version: 220 })
], EmailRequest.prototype, "defaultReplacements", void 0);
//# sourceMappingURL=EmailRequest.js.map